import React, { useState } from "react";
import { X, ChevronLeft, ChevronRight, Undo2, Loader2, Download, FileArchive } from "lucide-react";
import { proxied } from "@/lib/imageProxy";
import api from "@/lib/api";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}`;

async function downloadImage(url, filename) {
  try {
    // Ha relatív /api/files/... URL, absolut-ra alakítjuk
    const abs = url.startsWith("http") ? url : `${API_BASE}${url}`;
    const r = await fetch(abs, { credentials: "omit" });
    if (!r.ok) throw new Error("fetch fail");
    const blob = await r.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Eredeti képek galéria — csak akkor jelenik meg, ha a car-nak van `original_images` listája
 * (azaz futott már AI háttércsere). Lightbox nézettel + 1 kattintásos revert opcióval.
 */
export function OriginalSnapshotGallery({ car, onChange }) {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [reverting, setReverting] = useState(false);
  const [zipping, setZipping] = useState(false);

  const originals = Array.isArray(car?.original_images) ? car.original_images : [];
  if (!car?.addon_ai_photo_bg || originals.length === 0) return null;

  const doRevert = async () => {
    if (!window.confirm(`Visszaállítod mind a(z) ${originals.length} képet az eredetire?`)) return;
    setReverting(true);
    try {
      const r = await api.post(`/my/cars/${car.id}/ai-bg/revert`);
      onChange?.({ ...car, image: r.data.images?.[0], images: r.data.images });
      toast.success("Visszaállítva az eredeti képekre");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Visszaállítás sikertelen");
    } finally { setReverting(false); }
  };

  return (
    <div className="mt-2 pt-2 border-t border-white/5" data-testid={`original-snapshot-block-${car.id}`}>
      <button
        type="button"
        data-testid={`original-snapshot-toggle-${car.id}`}
        onClick={() => setOpen(true)}
        className="w-full inline-flex items-center justify-between gap-2 py-2 px-3 rounded-lg text-xs bg-white/[0.03] hover:bg-white/[0.06] border border-white/5"
      >
        <span className="inline-flex items-center gap-2">
          <div className="flex -space-x-1.5">
            {originals.slice(0, 3).map((src, i) => (
              <div key={i} className="w-6 h-6 rounded-md overflow-hidden border border-anthracite-900 bg-anthracite-800">
                <img src={proxied(src)} alt="" referrerPolicy="no-referrer" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
          <span className="text-slate-200">Eredeti képek</span>
        </span>
        <span className="text-electric">{originals.length} kép →</span>
      </button>

      {open && (
        <div data-testid={`original-snapshot-lightbox-${car.id}`} className="fixed inset-0 z-[105] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-anthracite-900 border border-white/10 rounded-3xl w-full max-w-3xl overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <div>
                <div className="font-heading text-base text-white">Eredeti képek — {car.make} {car.model}</div>
                <div className="text-[11px] text-slate-400">{idx + 1} / {originals.length} · AI háttércsere ELŐTTI állapot</div>
              </div>
              <button
                type="button"
                data-testid={`original-snapshot-close-${car.id}`}
                onClick={() => setOpen(false)}
                className="w-9 h-9 rounded-full bg-white/[0.05] hover:bg-white/[0.1] flex items-center justify-center text-slate-300"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="relative bg-anthracite-800">
              <div className="aspect-video max-h-[60vh]">
                <img
                  src={proxied(originals[idx])}
                  alt={`eredeti ${idx + 1}`}
                  referrerPolicy="no-referrer"
                  className="w-full h-full object-contain"
                />
              </div>
              {originals.length > 1 && (
                <>
                  <button
                    type="button"
                    data-testid={`original-snapshot-prev-${car.id}`}
                    onClick={() => setIdx((i) => (i - 1 + originals.length) % originals.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    data-testid={`original-snapshot-next-${car.id}`}
                    onClick={() => setIdx((i) => (i + 1) % originals.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>
            {/* Thumbnail strip */}
            {originals.length > 1 && (
              <div className="p-3 border-t border-white/5 flex gap-2 overflow-x-auto">
                {originals.map((src, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setIdx(i)}
                    className={`shrink-0 w-16 h-12 rounded-md overflow-hidden border ${i === idx ? "border-electric ring-1 ring-electric/40" : "border-white/10 opacity-60 hover:opacity-100"}`}
                  >
                    <img src={proxied(src)} alt="" referrerPolicy="no-referrer" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <div className="p-4 border-t border-white/5 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid={`original-snapshot-download-${car.id}`}
                onClick={async () => {
                  const ok = await downloadImage(originals[idx], `autopark-eredeti-${car.make}-${car.model}-${idx + 1}.jpg`);
                  if (ok) toast.success("Kép letöltve");
                  else toast.error("Letöltés sikertelen");
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-slate-200 text-xs"
              >
                <Download className="w-3.5 h-3.5" />
                Letöltés
              </button>
              <button
                type="button"
                data-testid={`original-snapshot-download-all-${car.id}`}
                disabled={zipping}
                onClick={async () => {
                  setZipping(true);
                  try {
                    const r = await api.get(`/my/cars/${car.id}/originals.zip`, { responseType: "blob" });
                    const objectUrl = URL.createObjectURL(r.data);
                    const a = document.createElement("a");
                    a.href = objectUrl;
                    a.download = `autopark-eredeti-${car.make}-${car.model}.zip`.replace(/\s+/g, "-").toLowerCase();
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
                    toast.success("ZIP letöltve");
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "ZIP letöltés sikertelen");
                  } finally { setZipping(false); }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-slate-200 text-xs disabled:opacity-50"
              >
                {zipping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileArchive className="w-3.5 h-3.5" />}
                {zipping ? "ZIP készül…" : `Mind letöltése ZIP (${originals.length})`}
              </button>
              <button
                type="button"
                data-testid={`original-snapshot-revert-${car.id}`}
                onClick={doRevert}
                disabled={reverting}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 hover:bg-red-500/20 border border-red-400/30 text-red-300 text-xs disabled:opacity-50"
              >
                {reverting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
                {reverting ? "Visszaállítás…" : "Aktuális képek visszaállítása erre"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
